<template>
  <div className="animated">
    <i class="icon-menu mr-1"></i>Product Orders
    <b-alert show variant="info" class="mt-3">
      This table shows all paid orders for products. Use the filters to narrow results. Select one or more orders to export or mark as delivered.
    </b-alert>
    <b-alert show variant="warning" class="mt-3">
      <i class="icon-exclamation mr-1"></i> This feature is still in its beta phase! <router-link :to="{name: 'businessItemList'}">Click here to manage products</router-link>.
    </b-alert>
    <hr />
    <!-- FILTERS -->
    <b-row class="mb-3">
      <b-col cols="12" lg="3">
        <b-form-group
          description="Filter by event or leave as '-- Active Events --'."
          label="Event"
          label-for="table-registrations-event"
          label-sr-only
        >
          <b-form-select
            id="table-registrations-event"
            v-model="queryFilter['items.businessItemEvent']"
            :options="events"
            value-field="id"
            text-field="title" 
            @input="(v) => {v ? queryFilter['event.status'] = null: null}"
          >
            <template v-slot:first>
              <option :value="null">-- Active Events --</option>
            </template>
          </b-form-select>
        </b-form-group>
      </b-col>

      <b-col cols="6" lg="4">
        <b-form-group
          description="Minimum date. Leave blank to omit."
          label="Date Start"
          label-for="table-addOns-date-1"
          class="mx-1"
          label-sr-only
        >
          <b-input-group prepend="Start">
            <b-input type="date" id="table-addOns-date-1" v-model="queryFilter['datePaid[after]']" /> 
          </b-input-group>
        </b-form-group>
      </b-col>
      <b-col cols="6" lg="4">
        <b-form-group
          description="Maximum date. Leave blank to omit."
          label="Date End"
          label-for="table-addOns-date-2"
          class="mx-1"
          label-sr-only
        >
          <b-input-group prepend="End">
            <b-input type="date" id="table-addOns-date-2" v-model="queryFilter['datePaid[before]']" />
          </b-input-group>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row>
      <b-col>
        <b-dropdown id="dropdown-1" text="With Selected..." class="m-md-2">
          <b-dropdown-item @click="doAction(3)">Mark Delivered</b-dropdown-item>
          <b-dropdown-item @click="doAction(1)">Export</b-dropdown-item>
          <b-dropdown-item @click="doAction(2)">Export and Mark Delivered</b-dropdown-item>
        </b-dropdown>
      </b-col>
      <b-col class="text-right"></b-col>
    </b-row>

    <v-server-table
      ref="table-add_on_orders" 
      id="table-add_on_orders" 
      class="bg-light p-2 mb-4" 
      name="dataTableEventAddOnOrders" 
      :url="url"
      :columns="columns"
      :options="options" 
      :useVuex="true"
      :theme="theme" 
      :preserveState="preserveState"
      @select="selected = $event"
    >
      <template slot="id" slot-scope="props">
        <router-link v-b-popover.hover="'View the invoice'" v-if="props.row.id" :to="{name: 'invoiceDetails', params: { iid: props.row.id }}">{{ props.row.id }}</router-link>
      </template>
    </v-server-table>

    <!-- EXPORT BUTTON -->
    <download-excel
      style="display: none"
      ref="download-excel"
      :fetch="fetchFile"
      :fields="fileFields"
      :before-generate="fileStart"
      :before-finish="fileFinish"
      :name="fileName + '.' + ext"
      :title="fileTitle"
      :worksheet="worksheetTitle"
      :type="ext"
    >
      <span :title="'Download Participants (' + ext.toUpperCase() + ')'" v-html="btnLabel"></span>
    </download-excel>
  </div>
</template>

<script>
// @TODO rename to ItemsOrders?
import Vue from 'vue'
import { mapGetters, mapActions } from 'vuex'
import { HTTP } from '../../services/http-common'
import { ServerTable } from 'vue-tables-2-premium'

Vue.use(ServerTable)
const DEFAULT_PER_PAGE = 30

export default {
  name: 'AddOnOrders',
  props: {
    eid: {
      type: Number,
      required: false,
      default: null
    }
  },
  data () {
    return {
      fileName: 'nzs-product-orders',
      fileTitle: 'Product Orders',
      worksheetTitle: 'Product Orders',
      btnLabel: '<i class="fa fa-download"></i> Download',
      fileFields: {
        'Invoice': 'id',
        'Order': 'item',
        'Date Paid': {
          field: 'datePaid',
          callback (datePaid) {
            return datePaid ? datePaid.substring(0, 10) : 'n/a'
          }
        }
      },
      ext: 'xls',
      options: {
        headings: {
          id: 'Invoice',
          item: 'Order',
          datePaid: 'Date Paid'
        },
        sortable: ['id', 'item', 'datePaid'],
        filterable: ['id', 'item', 'datePaid'],
        perPage: DEFAULT_PER_PAGE,
        perPageValues: [30,60,90,250,500],
        pagination: {
          chunk: 5
        },
        selectable:{
          mode: 'multiple',
          // only: (row) => {
          //   return row.invoice.transactionId === null
          // },
          selectAllMode: 'page',
          programmatic: false
        },
        debounce: 750,
        async requestFunction (data) {
          return await HTTP.get(this.url, {
              params: data
          }).catch(function (e) {
              this.dispatch('error', e)
          }.bind(this))
        },
        requestAdapter (data) {
          let query = {
            page: data.page ? data.page : '1',
            itemsPerPage: data.limit ? data.limit : DEFAULT_PER_PAGE,
            // pagination: false,
          }
          if (data.query !== '') {
            query['search_filter'] = data.query
          }
          if (data.orderBy) {
            if (data.orderBy === 'item') data.orderBy = 'items.item'
            let param = 'order[' + data.orderBy + ']'
            query[param] = data.ascending ? 'asc' : 'desc'
          } else {
            query['order[items.item]'] = 'asc'
          }
          return query
        },
        responseAdapter ({ data }) {
          let ret = []
          for (const inv of data['hydra:member']) {
            // add each item as a separate row
            for (const item of inv.items) {
              if (item.businessItem) {
                ret.push({
                  id: inv.id,
                  itemId: item.id,
                  item: item.item,
                  datePaid: inv.datePaid ? inv.datePaid.substring(0, 10) : 'n/a',
                })
              }
            }
          }
          return {
            data: ret,
            count: data['hydra:totalItems']
          }
        },
      },
      queryFilter: {
        id: null,
        'exists[transactionId]': true,
        'items.item': null,
        'datePaid[after]': null,
        'datePaid[before]': null,
        'items.businessItemEvent': null
      },
      theme: 'bootstrap4',
      preserveState: true,
      selected: []
    }
  },
  computed: {
    ...mapGetters([
      'events'
    ]),
    url () {
      const queryFilter = { ...this.queryFilter }
      const params = {}
      for (const k in queryFilter) {
        if (queryFilter[k]) params[k] = queryFilter[k]
      }
      //params['items.registration.event'] = this.$route.params.eid || null
      params['business'] = this.currentBusiness.id
      params['exists[items.businessItem]'] = true
      params['items.businessItemDelivered'] = false
      const query = Object.keys(params).map(key => key + '=' + params[key]).join('&')
      return '/invoices?' + query
    },
    columns () {
      let columns = [
        'id',
        'item',
        'datePaid'
      ]
      return columns
    },
  },
  async mounted () {
    // load events for filter
    await this.getEvents({
      business: this.currentBusiness.id,
      status: [1,2]
    })
    if (this.eid) {
      this.queryFilter['items.businessItemEvent'] = this.eid
    }
  },
  methods: {
    ...mapActions([
      'getEvents',
      'putInvoiceItem'
    ]),
    doAction (actionType) {
      switch (actionType) {
        case 1:
          this._exportOrders()
          break
        case 2:
          this._exportOrders()
          this._markDelivered()
          break
        case 3:
          this._markDelivered()
          break
      }
    },
    async _markDelivered () {
      if (!this.checkSelected()) return

      let promises = []
      for (let k in this.selected) {
        if (!this.selected[k].itemId) continue
        promises.push(this.putInvoiceItem({
          id: this.selected[k].itemId,
          businessItemDelivered: true
        }))
      }
      await Promise.all(promises).then(() => {
        this.$bvToast.toast('Orders marked as delivered', {
          title: 'Success',
          variant: 'success',
          solid: true
        })
      }).catch((e) => {
        this.$bvToast.toast('Error marking orders as delivered', {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
      })
      this.$refs['table-add_on_orders'].refresh()
    },
    _exportOrders () {
      if (!this.checkSelected()) return
      this.$refs['download-excel'].generate()
    },
    checkSelected () {
      if (this.selected.length < 1) {
        this.$bvToast.toast('No orders selected', {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
        return false
      }
      return true
    },
    fetchFile () {
      if (!this.checkSelected()) return []

      return this.selected
    },
    fileStart () {
      this.$bvToast.toast('Export started', {
        title: 'Success',
        variant: 'success',
        solid: true
      })
    },
    fileFinish () {
      this.$bvToast.toast('Export finished', {
        title: 'Success',
        variant: 'success',
        solid: true
      })
    }
  },
}
</script>
